import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail, setStoredToken } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = guest, object = logged-in user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  // After a successful login, optionally invoke a callback (e.g. continue to checkout).
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, []);

  const persistTokens = (data) => {
    if (data?.access_token) setStoredToken(data.access_token);
    const clean = { ...data };
    delete clean.access_token;
    delete clean.refresh_token;
    return clean;
  };

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const clean = persistTokens(data);
      setUser(clean);
      return { ok: true, user: clean };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      const clean = persistTokens(data);
      setUser(clean);
      return { ok: true, user: clean };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Network failure on logout is harmless — we still clear the client side.
      if (process.env.NODE_ENV === "development") console.warn("[auth] logout request failed:", e?.message);
    }
    setStoredToken(null);
    setUser(false);
  }, []);

  const requireAuth = useCallback((action) => {
    if (user) {
      action?.();
      return;
    }
    if (typeof action === "function") setPendingAction(() => action);
    setModalOpen(true);
  }, [user]);

  const openAuth = useCallback(() => {
    setPendingAction(null);
    setModalOpen(true);
  }, []);

  const closeAuth = useCallback(() => {
    setModalOpen(false);
  }, []);

  const onAuthSuccess = useCallback(() => {
    setModalOpen(false);
    if (pendingAction) {
      const fn = pendingAction;
      setPendingAction(null);
      // Defer one tick so the user state has flushed into context.
      setTimeout(() => fn(), 0);
    }
  }, [pendingAction]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        modalOpen,
        login,
        register,
        logout,
        openAuth,
        closeAuth,
        requireAuth,
        onAuthSuccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
